import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  BarChart3, 
  PieChart, 
  TrendingUp, 
  TrendingDown,
  Users, 
  Building2, 
  DollarSign,
  Calendar,
  MapPin,
  Download
} from 'lucide-react';
import { enhancedOfficeData, locations } from '../data/locationData';

const OccupancyReporting = ({ selectedLocation = 'All Locations' }) => {
  const [reportType, setReportType] = useState('overview');
  const [timeFrame, setTimeFrame] = useState('current');

  // Calculate occupancy metrics
  const calculateMetrics = (locationFilter = 'All Locations') => {
    let offices = enhancedOfficeData;
    
    if (locationFilter !== 'All Locations') {
      offices = offices.filter(office => office.location === locationFilter);
    }

    const totalOffices = offices.length;
    const totalDesks = offices.reduce((sum, office) => sum + office.desks, 0);
    
    const statusCounts = offices.reduce((acc, office) => {
      acc[office.status] = (acc[office.status] || 0) + 1;
      return acc;
    }, {});

    const desksByStatus = offices.reduce((acc, office) => {
      if (!acc[office.status]) acc[office.status] = 0;
      acc[office.status] += office.desks;
      return acc;
    }, {});

    const revenueByStatus = offices.reduce((acc, office) => {
      if (!acc[office.status]) acc[office.status] = 0;
      acc[office.status] += office.monthlyRate;
      return acc;
    }, {});

    const occupancyRate = totalDesks > 0 ? ((desksByStatus.LEASED || 0) / totalDesks * 100) : 0;
    const officeOccupancyRate = totalOffices > 0 ? ((statusCounts.LEASED || 0) / totalOffices * 100) : 0;

    return {
      totalOffices,
      totalDesks,
      statusCounts,
      desksByStatus,
      revenueByStatus,
      occupancyRate: occupancyRate.toFixed(1),
      officeOccupancyRate: officeOccupancyRate.toFixed(1),
      totalRevenue: revenueByStatus.LEASED || 0,
      potentialRevenue: Object.values(revenueByStatus).reduce((sum, val) => sum + val, 0)
    };
  };

  const metrics = calculateMetrics(selectedLocation);

  // Calculate location breakdown (only for All Locations view)
  const getLocationBreakdown = () => {
    if (selectedLocation !== 'All Locations') return [];
    
    return locations.slice(1).map(location => {
      const locationMetrics = calculateMetrics(location);
      return {
        location,
        ...locationMetrics
      };
    });
  };

  const locationBreakdown = getLocationBreakdown();

  const getStatusColor = (status) => {
    switch (status) {
      case 'LEASED': return 'text-emerald-600 bg-emerald-50';
      case 'VACANT': return 'text-blue-600 bg-blue-50';
      case 'PENDING': return 'text-amber-600 bg-amber-50';
      case 'UNAVAILABLE': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const exportReport = () => {
    // In real implementation, this would generate and download a report
    alert('Report export functionality would be implemented here');
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold text-slate-800">Occupancy Reporting</h1>
            {selectedLocation !== 'All Locations' && (
              <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300">
                <MapPin className="w-3 h-3 mr-1" />
                {selectedLocation}
              </Badge>
            )}
          </div>
          <p className="text-gray-600 mt-2">
            {selectedLocation === 'All Locations' 
              ? 'Comprehensive occupancy analytics across all Bond Collective locations' 
              : `Detailed occupancy report for ${selectedLocation}`}
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Select value={timeFrame} onValueChange={setTimeFrame}>
            <SelectTrigger className="w-40">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Current Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportReport} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Office Occupancy</p>
                <p className="text-3xl font-bold text-slate-800">{metrics.officeOccupancyRate}%</p>
                <p className="text-xs text-gray-500 mt-1">
                  {metrics.statusCounts.LEASED || 0} of {metrics.totalOffices} offices
                </p>
              </div>
              <Building2 className="w-8 h-8 text-slate-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Desk Occupancy</p>
                <p className="text-3xl font-bold text-slate-800">{metrics.occupancyRate}%</p>
                <p className="text-xs text-gray-500 mt-1">
                  {metrics.desksByStatus.LEASED || 0} of {metrics.totalDesks} desks
                </p>
              </div>
              <Users className="w-8 h-8 text-slate-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                <p className="text-3xl font-bold text-emerald-600">
                  ${metrics.totalRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  From leased offices
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Revenue Potential</p>
                <p className="text-3xl font-bold text-blue-600">
                  ${metrics.potentialRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  If 100% occupied
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Office Status Breakdown */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">
              Office Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(metrics.statusCounts).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(status).split(' ')[1]}`} />
                    <span className="font-medium text-slate-800">{status}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-slate-800">{count} offices</div>
                    <div className="text-sm text-gray-500">
                      {((count / metrics.totalOffices) * 100).toFixed(1)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Desk Utilization */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">
              Desk Utilization by Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(metrics.desksByStatus).map(([status, desks]) => (
                <div key={status} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(status).split(' ')[1]}`} />
                    <span className="font-medium text-slate-800">{status}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-slate-800">{desks} desks</div>
                    <div className="text-sm text-gray-500">
                      {((desks / metrics.totalDesks) * 100).toFixed(1)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Location Comparison (only for All Locations view) */}
      {selectedLocation === 'All Locations' && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">
              Location Performance Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Location</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Total Offices</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Total Desks</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Office Occupancy</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Desk Occupancy</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Monthly Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {locationBreakdown.map((location) => (
                    <tr key={location.location} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3 font-medium text-slate-800">{location.location}</td>
                      <td className="p-3 text-center">{location.totalOffices}</td>
                      <td className="p-3 text-center">{location.totalDesks}</td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center">
                          <span className="font-medium">{location.officeOccupancyRate}%</span>
                          {parseFloat(location.officeOccupancyRate) >= 80 ? (
                            <TrendingUp className="w-4 h-4 ml-1 text-green-600" />
                          ) : parseFloat(location.officeOccupancyRate) >= 60 ? (
                            <TrendingUp className="w-4 h-4 ml-1 text-amber-600" />
                          ) : (
                            <TrendingDown className="w-4 h-4 ml-1 text-red-600" />
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center">
                          <span className="font-medium">{location.occupancyRate}%</span>
                          {parseFloat(location.occupancyRate) >= 80 ? (
                            <TrendingUp className="w-4 h-4 ml-1 text-green-600" />
                          ) : parseFloat(location.occupancyRate) >= 60 ? (
                            <TrendingUp className="w-4 h-4 ml-1 text-amber-600" />
                          ) : (
                            <TrendingDown className="w-4 h-4 ml-1 text-red-600" />
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right font-medium text-emerald-600">
                        ${location.totalRevenue.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue Analysis */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800">
            Revenue Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 rounded-lg bg-emerald-50">
              <p className="text-sm text-emerald-600 font-medium">Current Revenue</p>
              <p className="text-2xl font-bold text-emerald-700">
                ${metrics.totalRevenue.toLocaleString()}
              </p>
              <p className="text-xs text-emerald-600 mt-1">From leased offices</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-blue-50">
              <p className="text-sm text-blue-600 font-medium">Potential Revenue</p>
              <p className="text-2xl font-bold text-blue-700">
                ${metrics.potentialRevenue.toLocaleString()}
              </p>
              <p className="text-xs text-blue-600 mt-1">If 100% occupied</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-amber-50">
              <p className="text-sm text-amber-600 font-medium">Revenue Gap</p>
              <p className="text-2xl font-bold text-amber-700">
                ${(metrics.potentialRevenue - metrics.totalRevenue).toLocaleString()}
              </p>
              <p className="text-xs text-amber-600 mt-1">Opportunity for growth</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OccupancyReporting;